import { $tenant, sendNotification, useApiClient, useNavigation, useTranslation, useUrlContext } from '@msdk/common'
import {
  dateDiff,
  DateTimeFieldFormat,
  DateTimeValue,
  Entity,
  EntityOperation,
  getStringFromDateTime,
  ServerOperationResult,
  StringValue,
} from '@msdk/definitions'
import { COLORS, createComponentWrapperWithRef, Divider, Link, styled, uiTokens } from '@msdk/ui'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import { getAdditionalContextFromLink, navigateToLink } from '../NotificationMessageRenderer/LinkNotificationUtils'
import { NotificationMarkdown } from '../NotificationMessageRenderer/NotificationMarkdown'

const MuiStyledContainer = styled('div', { shouldForwardProp: (prop) => prop !== '$hideDivider' })<{
  $hideDivider?: boolean
}>`
  display: flex;
  flex-direction: row;
  cursor: pointer;
  :hover {
    background-color: #0033a014;
  }
  padding: 16px 24px 16px 12px;
  border-bottom: 0.5px solid #c9d3de;
`

const NovaStyledContainer = styled('div', { shouldForwardProp: (prop) => prop !== '$hideDivider' })<{
  $hideDivider?: boolean
}>`
  display: flex;
  flex-direction: row;
  cursor: pointer;
  :hover {
    border-radius: ${uiTokens.radius.medium}px;
    background-color: ${uiTokens.uiControl.text.hover};
    border: 0;
  }
  padding: ${uiTokens.spacing.padding.semiMedium}px ${uiTokens.spacing.padding.medium}px;
  margin: 0 10px 0 ${uiTokens.spacing.padding.medium}px;
  border-bottom: ${(props) => (props.$hideDivider ? 'none' : `0.5px solid ${uiTokens.uiStroke.neutral}`)};
`

export const StyledContainer = createComponentWrapperWithRef(
  {
    mui: MuiStyledContainer,
    nova: NovaStyledContainer,
  },
  'StyledDivider',
)

const MessageContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`
const NotficationDotContainer = styled('div')`
  min-width: 26px;
  height: 22px;
  padding: 4px 5px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const NotificationDot = styled('div')`
  border-radius: 50%;
  width: 8px;
  height: 8px;
  background-color: ${COLORS.primary.main};
`

const NotificationReadDot = styled('div')`
  border-radius: 50%;
  width: 8px;
  height: 8px;
  border: 1px solid ${COLORS.primary.main};
`

const NotificationTitle = styled('h3')`
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  margin: 0;
`

const NotificationMessage = styled('span')`
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  margin: 0;
  color: rgba(0, 0, 0, 0.8);
  p {
    font-size: 16px;
    margin: 0 !important;
  }
`

const StyledLink = styled(Link)`
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  text-decoration: none;
  color: ${COLORS.primary.main};
`

const SendTime = styled('p')`
  font-size: 14px;
  font-weight: 400;
  line-height: 20.02px;
  color: ${COLORS.text.secondary};
  margin: 4px 0 0;
`

const MuiStyledDivider = styled(Divider)`
  margin-left: -24px;
  margin-right: -24px;
`

const NovaStyledDivider = styled(Divider)`
  border: none;
`

export const StyledDivider = createComponentWrapperWithRef(
  {
    mui: MuiStyledDivider,
    nova: NovaStyledDivider,
  },
  'StyledDivider',
)

type MarkReadResponse = { result: EntityOperation }

interface Props {
  notification: Entity
  hideDivider?: boolean
  onClose: () => void
  onNotificationUpdated: (id: string) => void
}

export function NotificationCard({ notification, hideDivider, onClose, onNotificationUpdated }: Props) {
  const t = useTranslation()
  const api = useApiClient()
  const navigate = useNavigation()
  const reactNavigate = useNavigate()
  const urlContext = useUrlContext()
  const tenant = useRecoilValue($tenant)

  const isNow = (sentDate: DateTimeValue): boolean => {
    const now = new DateTimeValue(new Date())
    const diffMs = dateDiff('milliseconds', now.value as any, sentDate.value as any)
    if (diffMs < 60000) {
      return true
    }
    return false
  }

  const isToday = (sentDate: DateTimeValue): boolean => {
    const now = new Date()
    const sendDate = sentDate.toJSDate()
    return (
      sendDate?.getDate() === now.getDate() &&
      sendDate?.getMonth() === now.getMonth() &&
      sendDate?.getFullYear() === now.getFullYear()
    )
  }

  const renderSendTime = (sentDate: DateTimeValue) => {
    if (!sentDate.value) {
      return null
    }

    const time = getStringFromDateTime(sentDate.value, { accuracy: sentDate.accuracy, format: DateTimeFieldFormat.t })
    let dateLabel = getStringFromDateTime(sentDate.value, {
      accuracy: sentDate.accuracy,
      format: DateTimeFieldFormat.g,
    })

    if (isToday(sentDate)) {
      dateLabel = t('TODAY_LBL', 'Today') + ', ' + time
    }

    if (isNow(sentDate)) {
      dateLabel = t('NOW_LBL', 'Now')
    }

    return <SendTime>{dateLabel}</SendTime>
  }

  const openLink = (value: StringValue) => {
    const link = value.value
    const additionalCtx = getAdditionalContextFromLink(link)
    onClose()
    navigateToLink(link, true, urlContext.context.sitemap, tenant, navigate, reactNavigate, additionalCtx)
  }

  const onNotificationClick = async () => {
    if (notification.fields['NOTIFICATION_STATUS']?.value === 'UNREAD') {
      const res = await api.read<MarkReadResponse>(`CMPS/api/notification/markasread/${notification.id}`)
      if ((res as MarkReadResponse).result.result === ServerOperationResult.Success) {
        onNotificationUpdated(notification.id)
      } else {
        console.error('[NotificationPopup] - error mark as read')
        sendNotification({
          message: t('CMPS_NOTIFICATION_MARK_READ_ERROR', 'Error while marking notification as read.'),
          status: 'error',
        })
      }
    }
  }

  const body = notification.fields['BODY']?.toStringForView() as string

  return (
    <>
      <StyledContainer onClick={onNotificationClick} $hideDivider={hideDivider}>
        <MessageContainer>
          <NotificationTitle>{notification.fields['TITLE']?.toStringForView()}</NotificationTitle>
          <NotificationMessage>
            <NotificationMarkdown body={body} onClose={onClose} />
            {notification.fields['BUTTON_LINK'] && (
              <StyledLink onClick={() => openLink(notification.fields['BUTTON_LINK'] as StringValue)}>
                {notification.fields['BUTTON_LABEL']?.toStringForView()}
              </StyledLink>
            )}
          </NotificationMessage>
          {renderSendTime(notification.fields['SENT_DATE'] as DateTimeValue)}
        </MessageContainer>
        <NotficationDotContainer>
          {notification.fields['NOTIFICATION_STATUS']?.value === 'UNREAD' && <NotificationDot />}
          {notification.fields['NOTIFICATION_STATUS']?.value === 'READ' && <NotificationReadDot />}
        </NotficationDotContainer>
      </StyledContainer>
      {!hideDivider && <StyledDivider />}
    </>
  )
}
